import { type ZodType, z } from "zod"

export const baseSchema = z.object({
  id: z.string(),
  row_version: z.number().optional(),
  backend: z.boolean().optional(),
})

type JsonDateConversion<T> = T extends z.ZodDate
  ? z.ZodString
  : T extends z.ZodOptional<infer U>
    ? z.ZodOptional<JsonDateConversion<U>>
    : T extends z.ZodNullable<infer U>
      ? z.ZodNullable<JsonDateConversion<U>>
      : T

export function schemaToJson<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
): z.ZodObject<{ [K in keyof T]: JsonDateConversion<T[K]> }> {
  const shape = schema.shape

  const newShape = Object.fromEntries(
    Object.entries(shape).map(([key, value]) => [
      key,
      convertDateToJsonString(value as ZodType),
    ]),
  ) as { [K in keyof T]: JsonDateConversion<T[K]> }

  return z.object(newShape)
}

function convertDateToJsonString(schema: ZodType): ZodType {
  if (schema instanceof z.ZodDate) {
    return z.iso.datetime({ offset: true })
  }

  if (schema instanceof z.ZodOptional) {
    const innerType = (schema as z.ZodOptional<z.ZodType>).unwrap()
    const convertedInner = convertDateToJsonString(innerType)
    return convertedInner.optional()
  }

  if (schema instanceof z.ZodNullable) {
    const innerType = (schema as z.ZodNullable<z.ZodType>).unwrap()
    const convertedInner = convertDateToJsonString(innerType)
    return convertedInner.nullable()
  }

  return schema
}

export function updateSchema<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
): z.ZodObject<{
  [K in keyof T]: K extends "id" ? T[K] : z.ZodOptional<T[K]>
}> {
  const shape = schema.shape
  const newShape = Object.fromEntries(
    Object.entries(shape).map(([key, value]) => [
      key,
      key === "id" ? value : (value as z.ZodType).optional(),
    ]),
  ) as { [K in keyof T]: K extends "id" ? T[K] : z.ZodOptional<T[K]> }
  return z.object(newShape)
}
